import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router";

import { Button } from "@/components/ui/Button";
import { BillsIcon, CheckIcon, EyeIcon, EyeOffIcon, LockIcon } from "@/components/ui/icons";
import { Input } from "@/components/ui/Input";
import { ApiError, apiFetch } from "@/lib/api";
import type { SignedInUser } from "@/lib/api-types";
import { CURRENT_ACTOR_QUERY_KEY, useCurrentActor } from "@/lib/current-actor";
import { labelFor } from "@/lib/field-labels";
import { safeRedirectTarget } from "@/lib/redirect-target";

import { AuthScreenLayout } from "./AuthScreenLayout";

/**
 * `/sign-in` (gap-fill spec, screen 1) — `POST /auth/sign-in`. The front
 * door: until this screen existed, nobody could get into PropDesk through a
 * browser at all (spec Intent).
 *
 * Every refusal — wrong credentials (401 `sign_in_failed`), a locked
 * account (429 `account_locked`, with the real computed unlock time already
 * baked into `error.message` by `app/domain/identity/authentication.py`'s
 * own `lockout_message`), or a saturated hasher (429 `too_many_requests`) —
 * renders `error.message` verbatim, unmodified (AC, VC-GF1-04). There is no
 * per-code branch here on purpose: the backend already deliberately makes
 * "wrong password" and "no such account" indistinguishable, and re-deriving
 * a different message per status code here would undo that.
 *
 * `?redirect=` is read once, validated through `safeRedirectTarget`
 * (judgement call 6) before it is used for anything, and re-validated at the
 * point of navigation rather than trusted from an earlier render — the same
 * value, computed fresh from the current URL each render, since this
 * component never mutates it.
 *
 * Story 15.7: `?reason=idle` — set only by `IdleSessionGuard`'s own real
 * timeout redirect, never by `RequireAuth.tsx`'s ordinary 401 gate (which
 * uses `?redirect=` alone, with no `reason`) — renders a specific, honest
 * "you were signed out due to inactivity" notice, deliberately never the
 * generic "please sign in" a plain, unexplained bounce would otherwise
 * leave someone to guess at (VC-15.7-09).
 */
const IDLE_SIGN_OUT_MESSAGE = "You were signed out due to inactivity.";
/**
 * Both fields get `invalid`/`aria-describedby` pointed at this same error on
 * any failure — never just one, mirroring this file's own deliberate
 * "never distinguish which field was wrong" anti-enumeration stance.
 */
const SIGN_IN_ERROR_ID = "sign-in-error";

/**
 * The Owner's own reference carried a 3-item trust strip below the button.
 * Real, verifiable claims about this app specifically, never the
 * reference's own generic/unverified ones (no "bank-level encryption" —
 * `app/domain/identity/authentication.py` hashes with argon2id and every
 * connection is TLS-terminated at the edge, which is what's actually true
 * and worth saying instead).
 */
const SIGN_IN_TRUST_ITEMS = [
  {
    Icon: LockIcon,
    label: "Private",
    description: "Built for one portfolio — never shared, never multi-tenant.",
  },
  {
    Icon: BillsIcon,
    label: "New Jersey–specific",
    description: "Alerts tied to real NJ landlord-tenant law, not generic reminders.",
  },
  {
    Icon: CheckIcon,
    label: "No tutorial needed",
    description: "Every screen answers one question, in plain language.",
  },
] as const;

export function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const destination = safeRedirectTarget(searchParams.get("redirect"));
  const signedOutForInactivity = searchParams.get("reason") === "idle";
  const canSubmit = email.trim() !== "" && password !== "";

  // Live-caught (gap-fill): a visit to /sign-in with an already-valid
  // session used to render the ordinary blank form with no acknowledgment
  // — indistinguishable from "did I get logged out?" for a user months
  // between sessions. `useCurrentActor` resolving with real data means the
  // one, authoritative role source (that hook's own doc comment) already
  // found a real session; mirrors `RequireAuth.tsx`'s own `<Navigate
  // replace>` pattern for the opposite direction.
  const currentActor = useCurrentActor();
  if (currentActor.data) {
    return <Navigate to={destination} replace />;
  }

  async function handleSubmit() {
    if (!canSubmit) {
      return;
    }
    setIsSubmitting(true);
    setError(undefined);
    try {
      await apiFetch<SignedInUser>("/auth/sign-in", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });
      // The rest of the app (NavRail's own name, every `useIsReadOnly`/
      // `useIsOwner` check) shares this one cached answer — invalidating it
      // is what makes the new session visible without a manual reload
      // (AC, VC-GF1-05).
      await queryClient.invalidateQueries({ queryKey: CURRENT_ACTOR_QUERY_KEY });
      navigate(destination, { replace: true });
    } catch (submitError) {
      setError(
        submitError instanceof ApiError ? submitError.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthScreenLayout
      title="Sign in to PropDesk"
      subtitle="Manage your properties, tenants, and leases in one place."
      belowForm={
        <div className="flex flex-col gap-4">
          {SIGN_IN_TRUST_ITEMS.map(({ Icon, label, description }) => (
            <div key={label} className="flex gap-3">
              <Icon className="mt-0.5 shrink-0 text-accent" width={20} height={20} />
              <div>
                <p className="text-body font-medium text-ink">{label}</p>
                <p className="text-meta text-ink-secondary">{description}</p>
              </div>
            </div>
          ))}
        </div>
      }
    >
      {signedOutForInactivity ? (
        <p role="status" className="text-meta text-ink-secondary">
          {IDLE_SIGN_OUT_MESSAGE}
        </p>
      ) : null}
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("email")}</span>
          <Input
            type="email"
            autoComplete="username"
            autoFocus
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            invalid={Boolean(error)}
            aria-describedby={error ? SIGN_IN_ERROR_ID : undefined}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("password")}</span>
          <div className="relative">
            <Input
              type={passwordVisible ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              invalid={Boolean(error)}
              aria-describedby={error ? SIGN_IN_ERROR_ID : undefined}
              className="pr-tap-target-min"
            />
            <button
              type="button"
              onClick={() => setPasswordVisible((visible) => !visible)}
              aria-label={passwordVisible ? "Hide password" : "Show password"}
              aria-pressed={passwordVisible}
              className="absolute inset-y-0 right-0 flex h-tap-target-min w-tap-target-min items-center justify-center text-ink-secondary hover:text-ink"
            >
              {passwordVisible ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
        </label>

        {error ? (
          <p id={SIGN_IN_ERROR_ID} role="alert" className="text-meta text-danger">
            {error}
          </p>
        ) : null}

        <Button type="submit" disabled={!canSubmit || isSubmitting}>
          {isSubmitting ? "Signing in…" : "Sign in"}
        </Button>
      </form>

      <p className="text-meta text-ink-secondary">
        <Link to="/forgot-password" className="underline hover:text-ink">
          Forgot your password?
        </Link>
      </p>
    </AuthScreenLayout>
  );
}
